import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Properties from "@/pages/dashboard/Properties";
import CreateProperty from "@/pages/dashboard/CreateProperty";
import ImportProperty from "@/pages/dashboard/ImportProperty";
import ReviewImport from "@/pages/dashboard/ReviewImport";
import PropertyEditor from "@/pages/dashboard/GuideBuilder";
import QRCodes from "@/pages/dashboard/QRCodes";
import Guides from "@/pages/dashboard/Guides";
import Settings from "@/pages/dashboard/Settings";
import Billing from "@/pages/dashboard/Billing";
import StructureHub from "@/pages/dashboard/StructureHub";
import WizardSetup from "@/pages/dashboard/WizardSetup";
import DemoExplore from "@/pages/dashboard/DemoExplore";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import PendingApproval from "@/pages/PendingApproval";
import GuestGuide from "@/pages/GuestGuide";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Faq from "@/pages/Faq";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CookiePolicy from "@/pages/CookiePolicy";
import { CookieBanner } from "@/components/CookieBanner";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.approval_status && user.approval_status !== "approved") return <Navigate to="/pending" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (user) {
    if (user.approval_status && user.approval_status !== "approved") return <Navigate to="/pending" replace />;
    return <Navigate to={user.onboarded ? "/dashboard" : "/onboarding"} replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <CookieBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/pending" element={<PendingApproval />} />

        <Route path="/faq" element={<Faq />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/termini" element={<Terms />} />
        <Route path="/cookie" element={<CookiePolicy />} />

        <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
          <Route index element={<Overview />} />
          <Route path="properties" element={<Properties />} />
          <Route path="properties/new" element={<CreateProperty />} />
          <Route path="properties/import" element={<ImportProperty />} />
          <Route path="properties/review" element={<ReviewImport />} />
          <Route path="guides" element={<Guides />} />
          <Route path="qr" element={<QRCodes />} />
          <Route path="settings" element={<Settings />} />
          <Route path="billing" element={<Billing />} />
          <Route path="admin" element={<AdminUsers />} />
        </Route>
        <Route path="/dashboard/properties/:id/wizard" element={<Protected><WizardSetup /></Protected>} />
        <Route path="/dashboard/properties/:id" element={<Protected><StructureHub /></Protected>} />
        <Route path="/dashboard/properties/:id/edit" element={<Protected><PropertyEditor /></Protected>} />
        <Route path="/dashboard/demo" element={<Protected><DemoExplore /></Protected>} />

        <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
        <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />

        <Route path="/g/:slug" element={<GuestGuide />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
